import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, MapPin } from 'lucide-react';
import { FadeUp, SlideLeft, SlideRight } from '../../../components/shared/Motion';
import { EditableText, HrefPill } from '../../components';
import { getIcon } from '../iconMap';

function ContactLocate({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const contacts = data.contacts || [];
  const updateContact = (i, patch) => {
    const next = contacts.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ contacts: next });
  };
  const CTAIcon = data.cta?.icon ? getIcon(data.cta.icon) : Compass;

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="container-page">
        <FadeUp className="max-w-3xl mb-14">
          <span className="eyebrow"><span className="h-px w-8 bg-olive" />
            <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
          </span>
          <h2 className="section-title mt-4">
            <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
            <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
          </h2>
          <p className="section-lead">
            <EditableText value={data.subheading} onChange={(t) => update({ subheading: t })} as="span" multiline />
          </p>
        </FadeUp>

        <div className="grid lg:grid-cols-12 gap-12 items-stretch">
          <SlideLeft className="lg:col-span-5">
            <div className="space-y-5">
              {contacts.map((c, i) => {
                const CI = getIcon(c.icon);
                return (
                  <FadeUp key={i} delay={i * 0.1}>
                    <div className="flex items-start gap-5 p-5 rounded-2xl border border-olive/10 hover:border-accent/40 hover:bg-olive-light/40 transition-all duration-300">
                      <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-olive-deep text-accent shadow-glow">
                        <CI size={20} />
                      </div>
                      <div>
                        <EditableText value={c.title} onChange={(t) => updateContact(i, { title: t })} as="div" className="text-[10px] font-mono uppercase tracking-[0.32em] text-olive" />
                        {c.href ? (
                          <a href={c.href} className="block mt-1 text-ink font-semibold whitespace-pre-line hover:text-olive transition">
                            <EditableText value={c.body} onChange={(t) => updateContact(i, { body: t })} as="span" multiline />
                          </a>
                        ) : (
                          <EditableText value={c.body} onChange={(t) => updateContact(i, { body: t })} as="div" multiline className="mt-1 text-ink font-semibold whitespace-pre-line" />
                        )}
                        <HrefPill value={c.href} onChange={(h) => updateContact(i, { href: h })} placeholder="href (optional)" />
                      </div>
                    </div>
                  </FadeUp>
                );
              })}
              {data.cta && (
                <FadeUp delay={0.4}>
                  <div className="inline-flex flex-col">
                    <Link to={data.cta.href || '#'} className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-accent text-ink font-bold text-sm uppercase tracking-[0.16em] shadow-[0_14px_36px_-10px_rgba(197,120,99,0.7)] hover:shadow-[0_20px_50px_-10px_rgba(197,120,99,1)] hover:-translate-y-0.5 transition-all duration-300">
                      <CTAIcon size={16} />
                      <EditableText value={data.cta.label} onChange={(t) => update({ cta: { ...(data.cta || {}), label: t } })} as="span" />
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                    <HrefPill value={data.cta.href} onChange={(h) => update({ cta: { ...(data.cta || {}), href: h } })} />
                  </div>
                </FadeUp>
              )}
            </div>
          </SlideLeft>

          <SlideRight className="lg:col-span-7">
            <div className="relative h-full min-h-[460px] rounded-3xl overflow-hidden border border-olive/15 shadow-[0_30px_60px_-25px_rgba(46,58,24,0.35)]">
              <iframe
                title={data.map_label || 'Campus Map'}
                src={data.map_embed_src}
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <div className="absolute top-5 left-5 inline-flex flex-col">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 backdrop-blur text-olive-deep font-bold text-xs uppercase tracking-[0.16em] shadow-soft">
                  <MapPin size={14} className="text-accent" />
                  <EditableText value={data.map_label} onChange={(t) => update({ map_label: t })} as="span" />
                </div>
                <HrefPill value={data.map_embed_src} onChange={(h) => update({ map_embed_src: h })} placeholder="map embed src" />
              </div>
            </div>
          </SlideRight>
        </div>
      </div>
    </section>
  );
}

export default ContactLocate;
